import React, { useState } from 'react';
import DateRangePresets from '../DateRangePresets';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Alert,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { CATEGORIES } from '../../config/constants';

interface MapPageFiltersProps {
  categories: string[];
  onCategoryChange: (categories: string[]) => void;
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  onReset: () => void;
  onSearch: () => void;
}

const MapPageFilters: React.FC<MapPageFiltersProps> = ({
  categories,
  onCategoryChange,
  startDate,
  endDate,
  onDateChange,
  onReset,
  onSearch
}) => {
  const [dateError, setDateError] = useState<string | null>(null);
  const handleCategoryToggle = (category: string) => {
    const newCategories = categories.includes(category)
      ? categories.filter(c => c !== category)
      : [...categories, category];
    
    onCategoryChange(newCategories);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Filters
        </Typography>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={onReset}
          size="small"
        >
          Reset
        </Button>
      </Box>

      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="categories-content"
          id="categories-header"
        >
          <Typography>
            Categories {categories.length > 0 && `(${categories.length})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {CATEGORIES.map(category => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryToggle(category)}
                color={categories.includes(category) ? 'primary' : 'default'}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="date-range-content"
          id="date-range-header"
        >
          <Typography>
            Date Range {(startDate || endDate) && '(Set)'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <DateRangePresets 
              onSelect={(start: Date, end: Date) => {
                setDateError(null);
                onDateChange(
                  start.toISOString().split('T')[0],
                  end.toISOString().split('T')[0]
                );
              }}
            />
            <Divider />
            <Stack spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setDateError(null);
                  const newStartDate = e.target.value;
                  
                  if (endDate && new Date(newStartDate) > new Date(endDate)) {
                    setDateError("Start date must be before end date");
                    return;
                  }
                  
                  onDateChange(newStartDate, endDate);
                }}
                error={!!dateError}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                size="small"
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setDateError(null);
                  const newEndDate = e.target.value;
                  
                  if (startDate && new Date(newEndDate) < new Date(startDate)) {
                    setDateError("End date must be after start date");
                    return;
                  }
                  
                  onDateChange(startDate, newEndDate);
                }}
                error={!!dateError}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                size="small"
              />
            </Stack>
            {dateError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {dateError}
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={onSearch}
      >
        Search
      </Button>
    </Box>
  );
};

export default MapPageFilters;
